import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RequirementsService } from '../../services/requirements.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { SavedViewService } from '../../services/saved-view.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { RequirementStatusService } from '../../services/requirement-status.service';
import { RequirementTypeService } from '../../services/requirement-type.service';
import { Requirement, RequirementStatusOption, RequirementTypeOption } from '../../models/requirement.model';
import { SavedView } from '../../models/saved-view.model';
import { REQUIREMENT_TYPE_DEFAULTS } from '../../shared/requirement-type-defaults';
import { SavedViewOption } from '../../shared/saved-views/saved-views-menu.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { TypePillComponent } from '../../components/type-pill/type-pill.component';
import { PriorityPillComponent } from '../../components/priority-pill/priority-pill.component';
import { ReqStatusPillComponent } from '../../components/req-status-pill/req-status-pill.component';
import { ReqRow, StatusTab, TableSortCol, TabCounts } from './requirements.types';
import {
  buildRequirementViewFiltersJson,
  computeTabCounts,
  filterRows,
  parseRequirementViewFilters,
  PRIORITY_RANK,
  reqToRow,
  sortRows,
  statusTabFromParam,
} from './requirements.utils';
import { RequirementsFilterBarComponent } from './requirements-filter-bar/requirements-filter-bar.component';
import { RequirementsStatsBarComponent } from './requirements-stats-bar/requirements-stats-bar.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { RequirementTypeLabelPipe } from '../../shared/requirement-type-label.pipe';
import { RequirementStatusLabelPipe } from '../../shared/requirement-status-label.pipe';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const PAGE_SIZE_ROUTE_KEY = 'requirements';

@Component({
  selector: 'app-requirements',
  imports: [
    ListFocusScrollDirective,
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    AvatarComponent,
    CodeIdChipComponent,
    TypePillComponent,
    PriorityPillComponent,
    ReqStatusPillComponent,
    RequirementsFilterBarComponent,
    RequirementsStatsBarComponent,
    ConfirmDialogComponent,
    RequirementTypeLabelPipe,
    RequirementStatusLabelPipe,
  ],
  templateUrl: './requirements.component.html',
  styleUrl: './requirements.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private requirementsService = inject(RequirementsService);
  private statusService = inject(RequirementStatusService);
  private typeService = inject(RequirementTypeService);
  private translate = inject(TranslateService);
  private projectRoleService = inject(ProjectRoleService);
  private savedViewService = inject(SavedViewService);
  private pageSizePref = inject(PageSizePreferenceService);

  readonly isProjectReader = this.projectRoleService.isProjectReader;

  readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId: string;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly searchTerm = signal('');
  readonly statusTab = signal<StatusTab>(null);
  readonly typeFilter = signal<string | null>(null);
  readonly priorityFilter = signal<string | null>(null);
  readonly authorFilter = signal<string | null>(null);
  readonly statusValueFilter = signal<string | null>(null);
  private readonly requirements = signal<Requirement[]>([]);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));
  readonly issuesError = signal<string | null>(null);
  readonly showDeleteRequirement = signal(false);
  readonly requirementToDelete = signal<ReqRow | null>(null);
  readonly deleteRequirementBusy = signal(false);
  readonly deleteRequirementMessage = computed(() => {
    const r = this.requirementToDelete();
    if (!r) return '';
    return this.translate.instant('requirements.deleteRequirement.confirmation', { title: r.title });
  });

  readonly tableSort = signal<{ col: TableSortCol | null; dir: 'asc' | 'desc' }>({ col: null, dir: 'asc' });

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({
      name: v.name,
      summary: this.summarizeView(v),
    })),
  );

  readonly statusOptions = signal<RequirementStatusOption[]>([]);
  // Startliste fuer den Typ-Filter — passt zu den Default-Werten, die das BE
  // beim erstmaligen Anlegen eines Projekts seedet (NEX-1016). Wird nach dem
  // Laden durch die projekt-lokalen Werte ersetzt.
  private static readonly DEFAULT_TYPE_OPTIONS: RequirementTypeOption[] = REQUIREMENT_TYPE_DEFAULTS.map((t, i) => ({
    id: `default-${i}`,
    value: t.value,
    orderIndex: i,
  }));
  readonly typeOptions = signal<RequirementTypeOption[]>([...RequirementsComponent.DEFAULT_TYPE_OPTIONS]);

  readonly allRows = computed((): ReqRow[] => {
    this.currentLang();
    const options = this.statusOptions();
    return this.requirements().map((r) => reqToRow(r, this.translate, options));
  });

  readonly tabCounts = computed((): TabCounts => computeTabCounts(this.allRows()));

  readonly typeFilterOptions = computed<string[]>(() => {
    const ordered = [...this.typeOptions()].sort((a, b) => a.orderIndex - b.orderIndex).map((o) => o.value);
    const known = new Set(ordered);
    const extras = [
      ...new Set(
        this.allRows()
          .map((r) => r.typeLabel)
          .filter((v) => v && !known.has(v)),
      ),
    ].sort();
    return [...ordered, ...extras];
  });

  readonly priorityFilterOptions = computed(() => {
    const rankByLabel = new Map<string, number>();
    for (const row of this.allRows()) {
      if (!rankByLabel.has(row.priorityLabel)) {
        const rank = PRIORITY_RANK[row.rawPriority ?? ''] ?? 99;
        rankByLabel.set(row.priorityLabel, rank);
      }
    }
    return [...rankByLabel.keys()].sort((a, b) => (rankByLabel.get(a) ?? 99) - (rankByLabel.get(b) ?? 99));
  });

  readonly authorFilterOptions = computed(() =>
    [
      ...new Set(
        this.allRows()
          .map((r) => r.author)
          .filter((a): a is string => a !== null),
      ),
    ].sort(),
  );

  readonly statusValueFilterOptions = computed<string[]>(() => {
    const ordered = [...this.statusOptions()].sort((a, b) => a.orderIndex - b.orderIndex).map((o) => o.value);
    const known = new Set(ordered);
    const extras = [
      ...new Set(
        this.allRows()
          .map((r) => r.rawStatus)
          .filter((s): s is string => !!s && !known.has(s)),
      ),
    ].sort();
    return [...ordered, ...extras];
  });

  readonly filteredRows = computed(() =>
    filterRows(
      this.allRows(),
      this.searchTerm(),
      this.statusTab(),
      this.typeFilter(),
      this.priorityFilter(),
      this.authorFilter(),
      this.statusValueFilter(),
    ),
  );

  readonly sortedRows = computed(() => sortRows(this.filteredRows(), this.tableSort()));

  readonly paginatorLength = computed(() => this.sortedRows().length);
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.paginatorLength() / this.pageSize())));
  readonly rangeStart = computed(() => (this.paginatorLength() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1));
  readonly rangeEnd = computed(() => Math.min(this.paginatorLength(), (this.pageIndex() + 1) * this.pageSize()));
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly pagedRows = computed((): ReqRow[] => {
    const start = this.pageIndex() * this.pageSize();
    return this.sortedRows().slice(start, start + this.pageSize());
  });

  constructor() {
    this.projectId = this.route.snapshot.params['projectId'];
    this.statusTab.set(statusTabFromParam(this.route.snapshot.queryParamMap.get('status')));
  }

  ngOnInit(): void {
    this.loadRequirements();
    this.loadSavedViews();
    this.typeService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          if (options.length > 0) {
            this.typeOptions.set(options);
          }
        },
        error: () => {
          // Startliste bleibt sichtbar.
        },
      });
    this.statusService.list(this.projectId).subscribe({
      next: (options) => {
        this.statusOptions.set(options);
        const paramStatus = this.route.snapshot.queryParamMap.get('status');
        if (paramStatus) {
          this.statusTab.set(statusTabFromParam(paramStatus, options));
          const exactMatch = options.find((o) => o.value === paramStatus);
          if (exactMatch) {
            this.statusValueFilter.set(exactMatch.value);
          }
        }
      },
      error: () => this.statusOptions.set([]),
    });
  }

  private loadSavedViews(): void {
    this.savedViewService.list(this.projectId, 'REQUIREMENTS').subscribe({
      next: (views) => this.savedViews.set(views),
      error: () => this.savedViews.set([]),
    });
  }

  private summarizeView(view: SavedView): string {
    const filters = parseRequirementViewFilters(view.filtersJson);
    const parts = [
      this.statusFilterLabel(filters.statusFilter),
      filters.statusValueFilter,
      filters.typ,
      filters.priority,
      filters.author,
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('requirements.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  private statusFilterLabel(filter: string | null): string | null {
    switch (filter) {
      case 'offen':
        return this.translate.instant('requirements.tabs.open');
      case 'review':
        return this.translate.instant('requirements.tabs.review');
      case 'fertig':
        return this.translate.instant('requirements.tabs.done');
      default:
        return null;
    }
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseRequirementViewFilters(view.filtersJson);
    this.searchTerm.set(filters.query ?? '');
    this.statusTab.set(filters.statusFilter);
    this.statusValueFilter.set(filters.statusValueFilter);
    this.typeFilter.set(filters.typ);
    this.priorityFilter.set(filters.priority);
    this.authorFilter.set(filters.author);
    this.pageIndex.set(0);
    this.activeSavedViewName.set(view.name);
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildRequirementViewFiltersJson({
      query: this.searchTerm() || null,
      statusFilter: this.statusTab(),
      statusValueFilter: this.statusValueFilter(),
      typ: this.typeFilter(),
      priority: this.priorityFilter(),
      author: this.authorFilter(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'REQUIREMENTS', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('requirements.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('requirements.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
  }

  private loadRequirements(): void {
    this.requirementsService.list(this.projectId).subscribe({
      next: (reqs) => {
        this.requirements.set(reqs);
        this.loading.set(false);
      },
      error: () => {
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }

  onPageChange(index: number): void {
    if (index < 0 || index >= this.pageCount() || index === this.pageIndex()) {
      return;
    }
    this.pageIndex.set(index);
  }

  onPageSizeChange(size: number): void {
    if (!size || size === this.pageSize()) {
      return;
    }
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
  }

  sortBy(col: TableSortCol): void {
    const current = this.tableSort();
    this.tableSort.set({ col, dir: current.col === col && current.dir === 'asc' ? 'desc' : 'asc' });
  }

  onRowAction(row: ReqRow): void {
    if (row.reqId) {
      this.router.navigate(['/projects', this.projectId, 'requirements', row.reqId, 'edit']);
    }
  }

  onRowKeydown(event: Event, row: ReqRow): void {
    if (event.target !== event.currentTarget) return;
    this.onRowAction(row);
  }

  onDeleteRow(row: ReqRow): void {
    if (!row.reqId) return;
    this.requirementToDelete.set(row);
    this.showDeleteRequirement.set(true);
  }

  onConfirmDeleteRequirement(): void {
    const row = this.requirementToDelete();
    if (!row?.reqId) return;
    this.issuesError.set(null);
    this.deleteRequirementBusy.set(true);
    this.requirementsService.delete(this.projectId, row.reqId).subscribe({
      next: () => {
        this.deleteRequirementBusy.set(false);
        this.showDeleteRequirement.set(false);
        this.requirementToDelete.set(null);
        this.loadRequirements();
      },
      error: () => {
        this.deleteRequirementBusy.set(false);
        this.showDeleteRequirement.set(false);
        this.requirementToDelete.set(null);
        this.issuesError.set(this.translate.instant('requirements.deleteError'));
      },
    });
  }

  onCancelDeleteRequirement(): void {
    this.showDeleteRequirement.set(false);
    this.requirementToDelete.set(null);
    this.deleteRequirementBusy.set(false);
  }

  onCreate(): void {
    this.router.navigate(['/projects', this.projectId, 'requirements', 'new']);
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.clearActiveView();
  }

  setStatusTab(tab: StatusTab): void {
    this.statusTab.set(tab);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  setFilter(key: 'type' | 'priority' | 'author' | 'statusValue', value: string | null): void {
    if (key === 'type') this.typeFilter.set(value);
    else if (key === 'priority') this.priorityFilter.set(value);
    else if (key === 'author') this.authorFilter.set(value);
    else this.statusValueFilter.set(value);
    this.pageIndex.set(0);
    this.clearActiveView();
  }
}
